import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';

async function changePassword(userId, currentPassword, newPassword) {
  const res = await fetch(`${API_BASE}/auth/users/${userId}/password`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ currentPassword, newPassword })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Misslyckades');
  return data;
}

const ROLE_LABEL = { admin: 'Administratör', contentCreator: 'Redaktör', sekreterare: 'Sekreterare', readOnly: 'Läsbehörighet' };

export default function ProfileManager() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const notify = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleChange = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) { notify('Lösenorden matchar inte', 'error'); return; }
    if (form.next.length < 8) { notify('Minst 8 tecken', 'error'); return; }
    setSaving(true);
    try {
      await changePassword(user?.id, form.current, form.next);
      setForm({ current: '', next: '', confirm: '' });
      notify('Lösenord uppdaterat');
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '2rem 1.5rem' }}>
      {toast && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 1000,
          background: toast.type === 'error' ? 'var(--danger)' : 'var(--forest)',
          color: '#fff', padding: '0.85rem 1.25rem', borderRadius: 6,
          boxShadow: '0 4px 20px rgba(0,0,0,.25)',
          display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.88rem'
        }}>
          <span>{toast.type === 'error' ? '✕' : '✓'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.6rem', color: 'var(--forest)', margin: '0 0 0.25rem' }}>
        Min profil
      </h2>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-soft)', marginTop: 0, marginBottom: '2rem' }}>
        Kontoinformation och lösenordsbyte
      </p>

      {/* Account info */}
      <div style={{
        background: 'var(--cream)', border: '1px solid var(--border)',
        borderRadius: 6, padding: '1.1rem 1.25rem', marginBottom: '2rem'
      }}>
        <Row label="Användarnamn" value={user?.username} />
        <Row label="Roll" value={ROLE_LABEL[user?.role] || user?.role} last />
      </div>

      {/* Change password */}
      <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.1rem', color: 'var(--forest)', margin: '0 0 1rem' }}>
        Byt lösenord
      </h3>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        <Field label="Nuvarande lösenord" type="password" value={form.current} onChange={handleChange('current')} required />
        <Field label="Nytt lösenord" type="password" value={form.next} onChange={handleChange('next')} required />
        <Field label="Bekräfta nytt lösenord" type="password" value={form.confirm} onChange={handleChange('confirm')} required />
        <button
          type="submit"
          disabled={saving || !form.current || !form.next || !form.confirm}
          className="btn btn-forest"
          style={{ alignSelf: 'flex-start', marginTop: '0.25rem', opacity: saving ? 0.6 : 1 }}
        >
          {saving ? 'Sparar...' : 'Byt lösenord'}
        </button>
      </form>
    </div>
  );
}

function Row({ label, value, last }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.4rem 0',
      borderBottom: last ? 'none' : '1px solid var(--border)'
    }}>
      <span style={{ fontSize: '0.8rem', color: 'var(--text-soft)' }}>{label}</span>
      <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text)' }}>{value}</span>
    </div>
  );
}

function Field({ label, type, value, onChange, required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        style={{
          padding: '0.6rem 0.8rem', border: '1px solid var(--border)',
          borderRadius: 4, fontSize: '0.9rem', fontFamily: 'Lato, sans-serif',
          background: 'var(--white)', color: 'var(--text)', outline: 'none'
        }}
      />
    </div>
  );
}
